import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import { brand } from '../theme/tokens';
import { brandAssets } from '../theme/brand-assets';
import { appVersion, appVersionDetail } from './app-build';
import { useLanguage } from './language-context';
import { usePlan } from './plan-context';
import { NavIcon } from './nav-icon';
import { SignOutLabel } from './sign-out-progress';
import { navGroups, visibleNavItems, isLocked } from './nav-items';

// The desktop glass rail (design M:90-157; CB-17 `rail.tsx`): brand block + role chip,
// the Work / Grow / Account groups separated by hairlines (no labels rendered, as in
// the design) plus the More group (Inbox, QR studio — DR-37), badges and lock glyphs,
// and the footer help card (Help · Contact) with Log out. Collapsible to an icon rail;
// the choice is a per-device convenience in localStorage (dash-layout owns it). The
// designer credit flip-card is not shipped (CB-17 note, INF-1); the brand lockup is the
// theme's artwork (theme/brand-assets.ts) and the version chip the build's
// (app-build.ts), never literals.
export function Rail({
  pending,
  isStaff,
  collapsed,
  onToggleCollapsed,
  onHelp,
  onContact,
  onLogout,
  loggingOut,
}: {
  pending: number;
  isStaff: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onHelp: () => void;
  onContact: () => void;
  onLogout: () => void;
  loggingOut: boolean;
}) {
  const { lang, t } = useLanguage();
  const { entitlements } = usePlan();
  const items = visibleNavItems(isStaff);
  const groups = navGroups
    .map((g) => ({ ...g, items: items.filter((i) => i.group === g.group) }))
    .filter((g) => g.items.length > 0);

  // Short viewports (a 1024 × 768 tablet): the nav scrolls under the help card. When
  // items sit below the fold, a "More" cue at the nav's foot says so and scrolls there.
  const navRef = useRef<HTMLElement>(null);
  const [below, setBelow] = useState(false);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const check = () => setBelow(nav.scrollTop + nav.clientHeight < nav.scrollHeight - 4);
    check();
    nav.addEventListener('scroll', check, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
    ro?.observe(nav);
    window.addEventListener('resize', check);
    return () => {
      nav.removeEventListener('scroll', check);
      ro?.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [collapsed, groups.length]);

  return (
    <aside
      className={`rail${collapsed ? ' is-collapsed' : ''}`}
      aria-label={t('merchant.nav.primary')}
    >
      <div className="rail__head">
        <span className="rail__brand">
          <img className="rail__logo" src={brandAssets.lockup} alt={brand.name} />
          <img className="rail__logo rail__logo--rev" src={brandAssets.lockupReversed} alt={brand.name} />
          {appVersion && (
            <span
              className="rail__ver"
              translate="no"
              title={formatMessage(lang, 'dc70.rail.version', { version: appVersionDetail ?? appVersion })}
            >
              V.{appVersion}
            </span>
          )}
        </span>
        <button
          type="button"
          className="rail__collapse"
          aria-label={t(collapsed ? 'dc70.rail.expand' : 'dc70.rail.collapse')}
          aria-expanded={!collapsed}
          onClick={onToggleCollapsed}
        >
          <NavIcon name="sidebar" size={16} />
        </button>
        <span className="rail__role" data-role={isStaff ? 'team' : 'owner'}>
          <NavIcon name="shield" size={10} strokeWidth={2} />
          <span className="rail__role-label">
            {t(isStaff ? 'dc70.role.team' : 'dc70.role.owner')}
          </span>
        </span>
      </div>

      <nav ref={navRef} className="rail__nav" aria-label={t('merchant.nav.primary')}>
        {groups.map((g) => (
          <div key={g.group} className="rail__group" role="group" aria-label={t(g.label)}>
            {g.items.map((item) => {
              const locked = isLocked(item, entitlements);
              const badge = item.badge === 'pending_proofs' && pending > 0 ? pending : 0;
              const label = t(item.key);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  title={collapsed ? label : undefined}
                  className={({ isActive }) =>
                    `rail__link${isActive ? ' is-active' : ''}${locked ? ' is-locked' : ''}`
                  }
                  data-nav={item.to}
                >
                  <NavIcon name={item.icon} className="rail__icon" />
                  <span className="rail__label">{label}</span>
                  {badge > 0 && (
                    <span
                      className="rail__count"
                      aria-label={formatMessage(lang, 'dc70.nav.pending_badge', { n: badge })}
                    >
                      {localiseDigits(lang, badge)}
                    </span>
                  )}
                  {locked && (
                    <span className="rail__lock" title={t('dc70.nav.locked')}>
                      <NavIcon name="lock" size={14} strokeWidth={2} />
                      <span className="visually-hidden">{t('dc70.nav.locked')}</span>
                    </span>
                  )}
                  {item.deferred && <span className="rail__soon">{t('dc70.nav.soon')}</span>}
                </NavLink>
              );
            })}
          </div>
        ))}
        {below && (
          <button
            type="button"
            className="rail__more"
            onClick={() => navRef.current?.scrollTo({ top: navRef.current.scrollHeight, behavior: 'smooth' })}
          >
            <span className="rail__more-pill">
              <NavIcon name="chevron" size={14} strokeWidth={2.2} />
              <span className="rail__more-label">{t('dc70.nav.more')}</span>
            </span>
          </button>
        )}
      </nav>

      <div className="rail__foot">
        <div className="rail__help">
          <div className="rail__help-text">
            <span className="rail__help-title">{t('dc70.help.card_title')}</span>
            <span className="rail__help-body">{t('dc70.help.card_body')}</span>
          </div>
          <div className="rail__help-actions">
            <button
              type="button"
              className="rail__help-btn rail__help-btn--primary"
              onClick={onHelp}
            >
              <NavIcon name="help" size={14} strokeWidth={2} />
              <span>{t('dc70.help.open')}</span>
            </button>
            <button type="button" className="rail__help-btn" onClick={onContact}>
              <NavIcon name="inbox" size={14} strokeWidth={2} />
              <span>{t('dc70.help.contact')}</span>
            </button>
          </div>
          <span className="rail__help-rule" aria-hidden="true" />
          <button type="button" className="rail__logout" onClick={onLogout} disabled={loggingOut} aria-busy={loggingOut}>
            <SignOutLabel label={t('dc70.help.logout')} icon={<NavIcon name="logout" size={15} strokeWidth={2} />} />
          </button>
        </div>
      </div>
    </aside>
  );
}
